import { useState, useEffect, useRef } from 'react';
import { FaChevronDown, FaQuestionCircle, FaSearch } from 'react-icons/fa';
import './FAQ.css';

const FAQ = ({ faqs = [], title = "Frequently Asked Questions", subtitle = "Find answers to common questions about our ALNoor Tutors" }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFaqs, setFilteredFaqs] = useState(faqs);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredFaqs(faqs);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = faqs.filter(
        (faq) =>
          faq.q.toLowerCase().includes(query) ||
          faq.a.toLowerCase().includes(query)
      );
      setFilteredFaqs(filtered);
    }
  }, [searchQuery, faqs]);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section 
      ref={sectionRef}
      className={`faq-component-section ${isVisible ? 'visible' : ''}`}
      id="faq"
    >
      <div className="container">
        {/* Header */}
        <div className="faq-component-header">
          <div className="faq-header-icon-wrapper">
            <FaQuestionCircle className="faq-header-icon" />
          </div>
          <span className="faq-component-badge">FAQ</span>
          <h2 className="faq-component-title">{title}</h2>
          <p className="faq-component-subtitle">{subtitle}</p>
        </div>

        {/* Search Bar */}
        <div className="faq-search-wrapper">
          <div className="faq-search-box">
            <FaSearch className="faq-search-icon" />
            <input
              type="text"
              placeholder="Search for questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="faq-search-input"
            />
          </div>
          {searchQuery && (
            <div className="faq-search-results">
              Found {filteredFaqs.length} {filteredFaqs.length === 1 ? 'result' : 'results'}
            </div>
          )}
        </div>

        {/* FAQ Grid */}
        <div className="faq-component-grid">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, index) => {
              const actualIndex = faqs.findIndex((f) => f.q === faq.q);
              const isOpen = openIndex === actualIndex;
              
              return (
                <div
                  key={actualIndex}
                  className={`faq-component-card ${isOpen ? 'is-open' : ''} ${isVisible ? 'visible' : ''}`}
                  style={{ animationDelay: `${(actualIndex % 8) * 0.05}s` }}
                >
                  <div className="faq-card-number">
                    {String(actualIndex + 1).padStart(2, '0')}
                  </div>
                  <div className="faq-card-content">
                    <button
                      className="faq-card-question"
                      onClick={() => toggleFaq(actualIndex)}
                      aria-expanded={isOpen}
                    >
                      <span className="faq-question-text">{faq.q}</span>
                      <span className={`faq-chevron-icon ${isOpen ? 'rotated' : ''}`}>
                        <FaChevronDown />
                      </span>
                    </button>
                    <div className={`faq-card-answer ${isOpen ? 'expanded' : ''}`}>
                      <div className="faq-answer-content">
                        <p>{faq.a}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="faq-no-results">
              <FaQuestionCircle className="faq-no-results-icon" />
              <h3>No results found</h3>
              <p>Try adjusting your search terms</p>
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="faq-help-text">
          <p>Still have questions? <a href="/contact">Contact us</a> and we'll be happy to help!</p>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
